import { fn, hash } from '@ember/helper';
import Modal from 'tio-reimagined/components/modal';
import { Section, VStack } from 'tio-reimagined/components/layout';
import { service } from '@ember/service';
import { t } from 'ember-intl';
import { tracked } from '@glimmer/tracking';
import AccountsLinkedLoansSummary from './linked-loans-summary';
import Component from '@glimmer/component';
import type { LoanSummary } from 'tio-common/types/loan-summary';
import type AccountModel from 'tio-data/models/account';
import type EmployeeService from 'tio-employee/services/employee';
import type PersonModel from 'tio-data/models/person';
import type RouterService from '@ember/routing/router';
import type SessionContextService from 'tio-employee/services/session-context';
// This is for types. Anything we have custom may show an error.
import type StoreService from 'ember-data/store';
import Button from 'tio-reimagined/components/button';
import { on } from '@ember/modifier';
import transitionTo from 'ember-router-helpers/helpers/transition-to';
import { PhCheckCircle } from 'ember-phosphor-icons';
import type { ObservableSource } from 'tio-employee/services/observability';

export interface LinkedAccountsCardSignature {
  Args: {
    accounts: AccountModel[];
    person: PersonModel;
    programName: string;
    loanSummary: LoanSummary;
    source: ObservableSource;
    providers?: string;
  };
  Element: HTMLElement;
}

export default class LinkedAccountsCard extends Component<LinkedAccountsCardSignature> {
  @service declare sessionContext: SessionContextService;
  @service declare store: StoreService;
  @service declare employee: EmployeeService;
  @service declare router: RouterService;

  @tracked showViewDetailsDialog = false;

  get activeAccounts() {
    return this.args.person.activeAccounts;
  }

  get accountInstitutionDisplayNames() {
    return this.activeAccounts.slice().map((account: AccountModel) => account.institutionName);
  }

  get hasActiveLinkedAccounts() {
    return !!this.activeAccounts.length;
  }

  <template>
    <Section>
      <:header>{{t "idr.accounts.default"}}</:header>
      <:body>
        {{#if this.hasActiveLinkedAccounts}}
          <p>{{t "idr.accounts.linked_below"}}</p>
          <div class="grid grid-cols-1 justify-items-center w-full">
            <ul class="font-bold min-w-[75%] mx-4">
              {{#each this.accountInstitutionDisplayNames as |name|}}
                <li class="my-2 p-2 bg-ocean-50 tio-flex-between">
                  <p class="flex-wrap">{{name}}</p>
                  <PhCheckCircle @size="24" @weight="fill" class="tio-fg-success" />
                </li>
              {{/each}}
            </ul>
            <div class="flex flex-col gap-y-4">
              <Button
                @intent="primary"
                class="w-full"
                {{on
                  "click"
                  (transitionTo
                    "authenticated.observability.upload"
                    (hash queryParams=(hash source=@source providers=@providers))
                  )
                }}
              >
                {{t "idr.accounts.add_more_accounts"}}
              </Button>
              <Button
                @intent="primary"
                @appearance="outlined"
                class="w-full"
                {{on "click" (fn (mut this.showViewDetailsDialog) true)}}
              >
                {{t "idr.accounts.view_details"}}
              </Button>
            </div>
          </div>
        {{else}}
          <VStack>
            <p>{{t "no_linked_accounts_alert.please_link_loans" program=@programName}}</p>
            <div>
              <Button
                @intent="primary"
                class="my-3 px-8"
                {{on
                  "click"
                  (transitionTo
                    "authenticated.observability.upload" (hash queryParams=(hash source=@source))
                  )
                }}
              >
                {{t "pslf_dashboard.actions.add_accounts"}}
              </Button>
            </div>
          </VStack>
        {{/if}}
      </:body>
    </Section>

    <Modal
      @isOpen={{this.showViewDetailsDialog}}
      @onClose={{fn (mut this.showViewDetailsDialog) false}}
      class="min-h-64 lg:min-w-5xl"
      @size="xl"
    >
      <:header>
        {{t "idr.accounts.accounts_linked"}}
      </:header>
      <:body>
        <AccountsLinkedLoansSummary
          @accounts={{@accounts}}
          @loanSummary={{@loanSummary}}
          @person={{@person}}
          @source={{@source}}
        />
      </:body>
    </Modal>
  </template>
}
